import {AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {MenuItem} from '@material-ui/core';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import {GenelcoBatchFile} from 'Enrollment/models/GenelcoBatchFile';
import {
  getGenelcoBatchFile,
  reGenerateBatchFile,
  reAuditBatchFile,
  changeGenelcoBatchFileFilters,
  changeGenelcoBatchFileSort,
  clearGenelcoBatchFiles,
  clearGenelcoBatchFileSort,
} from 'Enrollment/ducks/genelcoBatchFileManagement';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import * as GenelcoBatchFileFileActions from 'Enrollment/ducks/genelcoBatchFileManagement';
import {
  withDateFilterProperties,
  withInequalityFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import * as O from 'fp-ts/lib/Option';

type GenelcoBatchFileProps = {
  genelcoBatchFileMetaData: AsyncPage<Page<GenelcoBatchFile>>;
};

const GenelcoBatchFileTable = (props: GenelcoBatchFileProps) => {
  const dispatch = useDispatch();
  const {genelcoBatchFileFilters, isBatchRegenerationCompleted, isBatchReAuditCompleted, sort} = useSelector(
    (state: ReducerState) => state.genelcoBatchFileManagementState
  );

  const isRegenerated = (batchFileDate: Date) => isBatchRegenerationCompleted.some(date => date === batchFileDate);
  const isReAudited = (batchFileDate: Date) => isBatchReAuditCompleted.some(date => date === batchFileDate);

  const changeFilter = withValueChangedHook(
    genelcoBatchFileFilters as GenelcoBatchFileFileActions.GenelcoBatchFileFilters,
    changeGenelcoBatchFileFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: genelcoBatchFileFilters as GenelcoBatchFileFileActions.GenelcoBatchFileFilters,
  };

  return (
    <PagedView
      pagedViewKey="GenelcoBatchFileTable"
      data={props.genelcoBatchFileMetaData}
      sort={O.toUndefined(sort)}
      onChangeSort={sort => dispatch(changeGenelcoBatchFileSort(sort))}
      loadMore={page => dispatch(getGenelcoBatchFile(page, genelcoBatchFileFilters))}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Batch File Creation"
            default
            sortColumnKey="batchFileCreationDate"
            filterProperties={withDateFilterProperties(filterProperties, 'batchFileCreationDate')}
          >
            {genelcoBatchFiles => toFormattedDateTime(genelcoBatchFiles.batchFileCreationDate!)}
          </Column>
          <Column
            {...columnProps}
            title="Last Batch"
            default
          >
            {genelcoBatchFiles => toFormattedDateTime(genelcoBatchFiles.lastBatch!)}
          </Column>
          <Column
            {...columnProps}
            title="Last Audit"
            default
          >
            {genelcoBatchFiles => toFormattedDateTime(genelcoBatchFiles.lastAudit!)}
          </Column>
          <Column
            {...columnProps}
            title="Destination Folder"
            filterProperties={withStringFilterProperties(filterProperties, 'destinationFolder')}
          >
            {genelcoBatchFiles => genelcoBatchFiles.destinationFolder}
          </Column>
          <Column
            {...columnProps}
            title="Application Count"
            default
          >
            {genelcoBatchFiles => genelcoBatchFiles.applicationCount?.toString()}
          </Column>
          <Column
            {...columnProps}
            title="Applications Failed"
            default
          >
            {genelcoBatchFiles => genelcoBatchFiles.failedApplicationCount?.toString()}
          </Column>
          <Column {...columnProps} title="Actions" isActions default>
            {genelcoBatchFiles => (
              <>
                <ActionsMenu>
                  <MenuItem
                    id="menu-item-actions-batch-file-regenerate"
                    color="primary"
                    disabled={isRegenerated(genelcoBatchFiles.batchFileCreationDate!)}
                    onClick={() =>
                      dispatch(reGenerateBatchFile(genelcoBatchFileFilters, genelcoBatchFiles.batchFileCreationDate!))
                    }
                  >
                    Regenerate
                  </MenuItem>
                  <MenuItem
                    id="menu-item-actions-batch-file-re-audit"
                    color="primary"
                    disabled={
                      isReAudited(genelcoBatchFiles.batchFileCreationDate!) ||
                      genelcoBatchFiles.fileStatus?.id !== EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE
                    }
                    onClick={() =>
                      dispatch(reAuditBatchFile(genelcoBatchFileFilters, genelcoBatchFiles.batchFileCreationDate!))
                    }
                  >
                    Re-Audit
                  </MenuItem>
                </ActionsMenu>
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default GenelcoBatchFileTable;
